import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button'
import Icon from '@/components/Icon'
import Modal from '@/components/Modal'
import Price from '@/components/Price'
import useCheckout from '@/features/Checkout/hooks/useCheckout'
import type { Product, ProductConsumerCategory } from '@/lib/api.types'
import type { TicketPrice } from '@/types/tickets'

import TicketTypeCard from '../TicketTypeCard'
import {
  DetailsModalContent,
  DetailsModalDateTime,
  DetailsModalDivider,
  DetailsModalFooter,
  DetailsModalHero,
  DetailsModalInfo,
  DetailsModalMedia,
  DetailsModalSection,
  DetailsModalSummary,
  DetailsModalTitle
} from './DetailsModal.styles'

interface DetailsModalProps {
  title: string
  durationLabel: string
  coverImageUrl: string
  selectedDate: string
  selectedTime: string | null
  product: Product
  currency?: string
  maxTotalQty?: number | null
  onClose: () => void
}

const buildCartItemId = (productId: string, consumerCategoryId: number | string, date: string, startTime: string | null) => {
  const normalizedStartTime = startTime ?? 'whole-day'
  return `ticket:${productId}:${consumerCategoryId}:${date}:${normalizedStartTime}`
}

const buildEmptyQtyByCategoryId = (categories: ProductConsumerCategory[]) =>
  categories.reduce<Record<string, number>>((acc, category) => {
    acc[String(category.id)] = 0
    return acc
  }, {})

const getAllowStandalonePurchase = (category: ProductConsumerCategory) => {
  const categoryRecord = category as unknown as { settings?: unknown }
  const settings = categoryRecord.settings

  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return true
  }

  const allowStandalonePurchase = (settings as { allowStandalonePurchase?: unknown }).allowStandalonePurchase
  if (typeof allowStandalonePurchase === 'boolean') {
    return allowStandalonePurchase
  }

  return true
}

const normalizePrice = (value: unknown) => {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) {
    return 0
  }

  return numericValue >= 0 ? numericValue : 0
}

type ProductConsumerCategoryWithPricing = ProductConsumerCategory & {
  lowestPriceIn30Days?: {
    grossAmount?: string
  }
}

const extractTicketPrice = (
  product: Product,
  consumerCategory: ProductConsumerCategoryWithPricing | null,
  fallbackCurrency?: string
): TicketPrice => {
  const primaryCategory = (consumerCategory ?? product.consumerCategories?.[0]) as
    | {
        price?: {
          grossAmount?: string
          currency?: string
          taxPercentage?: number
          tax?: {
            value?: string
          }
        }
        lowestPriceIn30Days?: {
          grossAmount?: string
        }
      }
    | undefined

  const grossAmount = normalizePrice(primaryCategory?.price?.grossAmount ?? product.price?.grossAmount ?? product.defaultPrice)
  const lowestGrossAmountIn30Days = normalizePrice(
    primaryCategory?.lowestPriceIn30Days?.grossAmount ?? primaryCategory?.price?.grossAmount ?? grossAmount
  )
  const taxPercentage = normalizePrice(
    primaryCategory?.price?.tax?.value ?? primaryCategory?.price?.taxPercentage ?? product.price?.taxPercentage
  )
  const currency = primaryCategory?.price?.currency || product.price?.currency || fallbackCurrency || 'PLN'

  return {
    grossAmount,
    currency,
    taxPercentage,
    lowestGrossAmountIn30Days
  }
}

const formatSelection = (date: string, time: string | null, language: string) => {
  const parsedDate = new Date(`${date}T00:00:00`)
  if (Number.isNaN(parsedDate.getTime())) {
    return time || '--'
  }

  const locale = language.startsWith('pl') ? 'pl-PL' : 'en-US'
  const formattedDate = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long'
  }).format(parsedDate)

  if (!time) {
    return formattedDate
  }

  return `${formattedDate}, ${time}`
}

const DetailsModal = ({
  title,
  durationLabel,
  coverImageUrl,
  selectedDate,
  selectedTime,
  product,
  currency,
  maxTotalQty = null,
  onClose
}: DetailsModalProps) => {
  const { t, i18n } = useTranslation()
  const { cart, addToCart } = useCheckout()

  const selectedLabel = useMemo(
    () => formatSelection(selectedDate, selectedTime, i18n.language),
    [i18n.language, selectedDate, selectedTime]
  )

  const sortedConsumerCategories = useMemo(
    () =>
      [...(product.consumerCategories || [])].sort(
        (a: ProductConsumerCategory, b: ProductConsumerCategory) => (a.sortOrder || 0) - (b.sortOrder || 0)
      ),
    [product.consumerCategories]
  )
  const productId = String(product.id)
  const productUnlockId = Number(product.productId || product.id)
  const paymentMethod = product.defaultPaymentMethod || 'onSite'
  const availabilityType = selectedTime ? 'TIME_SLOT' : 'WHOLE_DAY'

  const pricesByCategoryId = useMemo(
    () =>
      sortedConsumerCategories.reduce<Record<string, TicketPrice>>((acc, category) => {
        acc[String(category.id)] = extractTicketPrice(product, category as ProductConsumerCategoryWithPricing, currency)
        return acc
      }, {}),
    [currency, product, sortedConsumerCategories]
  )

  const [pendingQtyByCategoryId, setPendingQtyByCategoryId] = useState<Record<string, number>>(() =>
    buildEmptyQtyByCategoryId(sortedConsumerCategories)
  )

  useEffect(() => {
    setPendingQtyByCategoryId(buildEmptyQtyByCategoryId(sortedConsumerCategories))
  }, [sortedConsumerCategories, selectedDate, selectedTime])

  const normalizedMaxTotalQty = useMemo(() => {
    const numericValue = Number(maxTotalQty)
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      return null
    }

    return Math.floor(numericValue)
  }, [maxTotalQty])

  const consumerCategoryIds = useMemo(
    () => new Set(sortedConsumerCategories.map(category => String(category.id))),
    [sortedConsumerCategories]
  )
  const allowStandaloneByCategoryId = useMemo(
    () =>
      sortedConsumerCategories.reduce<Record<string, boolean>>((acc, category) => {
        acc[String(category.id)] = getAllowStandalonePurchase(category)
        return acc
      }, {}),
    [sortedConsumerCategories]
  )
  const existingCartTotalQty = useMemo(
    () =>
      cart.reduce((sum, item) => {
        if (item.productId !== productId) {
          return sum
        }
        if (item.date !== selectedDate || item.startTime !== selectedTime) {
          return sum
        }
        if (!item.consumerCategoryId || !consumerCategoryIds.has(String(item.consumerCategoryId))) {
          return sum
        }

        return sum + Math.max(0, Number(item.qty) || 0)
      }, 0),
    [cart, consumerCategoryIds, productId, selectedDate, selectedTime]
  )
  const remainingCapacityForSession =
    normalizedMaxTotalQty === null ? null : Math.max(0, normalizedMaxTotalQty - Math.max(0, existingCartTotalQty))
  const pendingTotalQty = useMemo(
    () =>
      Object.values(pendingQtyByCategoryId).reduce((sum, qty) => {
        return sum + Math.max(0, qty || 0)
      }, 0),
    [pendingQtyByCategoryId]
  )

  const modalTotal = useMemo(
    () =>
      sortedConsumerCategories.reduce((sum, category) => {
        const qty = Math.max(0, pendingQtyByCategoryId[String(category.id)] || 0)
        const price = pricesByCategoryId[String(category.id)]
        return sum + qty * (price?.grossAmount || 0)
      }, 0),
    [pendingQtyByCategoryId, pricesByCategoryId, sortedConsumerCategories]
  )
  const modalTotalCurrency =
    pricesByCategoryId[String(sortedConsumerCategories[0]?.id)]?.currency || currency || product.price?.currency || 'PLN'

  const handleQtyChange = (consumerCategoryId: string, value: number) => {
    setPendingQtyByCategoryId(prev => {
      const nextValue = Math.max(0, value)
      const currentQty = Math.max(0, prev[String(consumerCategoryId)] || 0)
      const otherCategoriesQty = Object.entries(prev).reduce((sum, [categoryId, qty]) => {
        if (categoryId === String(consumerCategoryId)) {
          return sum
        }

        return sum + Math.max(0, qty || 0)
      }, 0)
      const allowStandalonePurchase = allowStandaloneByCategoryId[String(consumerCategoryId)] ?? true

      if (nextValue > currentQty && !allowStandalonePurchase && otherCategoriesQty <= 0) {
        return prev
      }

      if (remainingCapacityForSession === null) {
        return {
          ...prev,
          [String(consumerCategoryId)]: nextValue
        }
      }

      const maxForCategory = Math.max(0, remainingCapacityForSession - otherCategoriesQty)
      const clampedValue = Math.min(nextValue, maxForCategory)

      return {
        ...prev,
        [String(consumerCategoryId)]: clampedValue
      }
    })
  }

  const handleAddToCart = () => {
    let remainingCapacity = remainingCapacityForSession
    const requestedPendingTotal = sortedConsumerCategories.reduce(
      (sum, category) => sum + Math.max(0, pendingQtyByCategoryId[String(category.id)] || 0),
      0
    )

    sortedConsumerCategories.forEach(category => {
      const categoryId = String(category.id)
      const requestedSessionQty = Math.max(0, pendingQtyByCategoryId[categoryId] || 0)
      const requestedOtherCategoriesQty = Math.max(0, requestedPendingTotal - requestedSessionQty)
      const allowStandalonePurchase = allowStandaloneByCategoryId[categoryId] ?? true
      const sessionQty = remainingCapacity === null ? requestedSessionQty : Math.min(requestedSessionQty, remainingCapacity)
      const cartItemId = buildCartItemId(productId, category.id, selectedDate, selectedTime)
      const existingCartItem = cart.find(item => String(item.id) === cartItemId)
      const existingQty = Math.max(0, existingCartItem?.qty || 0)

      if (!allowStandalonePurchase && requestedOtherCategoriesQty <= 0) {
        return
      }

      if (sessionQty <= 0) {
        return
      }

      addToCart({
        id: cartItemId,
        productId,
        productName: product.name,
        coverImageUrl,
        name: category.name || product.name,
        price: pricesByCategoryId[categoryId] || extractTicketPrice(product, category as ProductConsumerCategoryWithPricing, currency),
        qty: existingQty + sessionQty,
        consumerCategoryId: category.id,
        date: selectedDate,
        startTime: selectedTime,
        pickupMethod: 'onSite',
        paymentMethod,
        orderItemComponents: product.productComponents ?? [],
        availabilityType,
        unlockId: productUnlockId,
        restricted: Boolean(product.requirePersonalData)
      })

      if (remainingCapacity !== null) {
        remainingCapacity = Math.max(0, remainingCapacity - sessionQty)
      }
    })

    onClose()
  }

  return (
    <Modal
      onClose={onClose}
      variant="full"
      showClose={true}
      footer={
        <DetailsModalFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <DetailsModalSummary>
            <span>{t('total')}:</span>
            <Price currency={modalTotalCurrency}>{modalTotal}</Price>
          </DetailsModalSummary>
          <Button onClick={handleAddToCart} disabled={modalTotal <= 0}>
            {t('addToCart')}
          </Button>
        </DetailsModalFooter>
      }
    >
      <DetailsModalContent>
        <DetailsModalHero>
          <DetailsModalMedia>
            <img src={coverImageUrl} alt={title} />
          </DetailsModalMedia>
          <DetailsModalInfo>
            <h3>{title}</h3>
            <p>{durationLabel}</p>
            <DetailsModalDateTime>
              <Icon name="clock" />
              <span>{selectedLabel}</span>
            </DetailsModalDateTime>
          </DetailsModalInfo>
        </DetailsModalHero>

        <DetailsModalDivider />

        <DetailsModalSection>
          <DetailsModalTitle>{t('selectTicketType')}</DetailsModalTitle>

          {sortedConsumerCategories.map(consumerCategory => (
            (() => {
              const consumerCategoryId = String(consumerCategory.id)
              const currentQty = Math.max(0, pendingQtyByCategoryId[consumerCategoryId] || 0)
              const otherCategoriesQty = Math.max(0, pendingTotalQty - currentQty)
              const allowStandalonePurchase = allowStandaloneByCategoryId[consumerCategoryId] ?? true
              const standaloneBlocked = !allowStandalonePurchase && otherCategoriesQty <= 0
              const maxForCategoryByCapacity =
                remainingCapacityForSession === null ? null : Math.max(0, remainingCapacityForSession - otherCategoriesQty)
              const capacityBlocked = maxForCategoryByCapacity !== null && currentQty >= maxForCategoryByCapacity
              const disablePlus = standaloneBlocked || capacityBlocked
              const disableMinus = currentQty <= 0

              return (
                <TicketTypeCard
                  key={`${String(product.productId || product.id)}:${consumerCategory.id}`}
                  product={product}
                  consumerCategory={consumerCategory}
                  qty={currentQty}
                  currency={currency}
                  disablePlus={disablePlus}
                  disableMinus={disableMinus}
                  onQtyChange={nextQty => handleQtyChange(consumerCategoryId, nextQty)}
                />
              )
            })()
          ))}
        </DetailsModalSection>
      </DetailsModalContent>
    </Modal>
  )
}

export default DetailsModal
